import React, { useEffect, useState, useRef } from "react";
import genericRepositoryService from "../../../services/genericRepositoryService";
import { IExportStatusResponse } from "../../../models/IExportStatusResponse";
import "./ExportStatusModal.css";

interface IExportStatusModalProps {
  isOpen: boolean;
  lessonVersionId: number;
  onClose: () => void;
}

const ExportStatusModal: React.FC<IExportStatusModalProps> = ({ isOpen, lessonVersionId, onClose }) => {
  const [status, setStatus] = useState<IExportStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [hasStartedProcessing, setHasStartedProcessing] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isOpen || !lessonVersionId) {
      return;
    }

    // Reset state when modal opens
    setHasStartedProcessing(false);
    setIsComplete(false);

    // Initial fetch
    fetchStatus();

    // Set up polling every 5 seconds
    intervalRef.current = setInterval(() => {
      fetchStatus();
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isOpen, lessonVersionId]);

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const shouldStopPolling = (data: IExportStatusResponse): boolean => {
    // Stop polling if:
    // 1. Job is published (success state) OR
    // 2. Processing has finished (isProcessing = false) AND we've seen it processing before
    // This prevents stopping when we check too early before the job is created
    return data.isPublished || (!data.isProcessing && hasStartedProcessing);
  };

  const handleSuccessfulResponse = (data: IExportStatusResponse) => {
    setStatus(data);
    setError(null);

    // Track if we've seen the job actually processing
    if (data.isProcessing) {
      setHasStartedProcessing(true);
    }

    if (shouldStopPolling(data)) {
      setIsComplete(true);
      stopPolling();
    }
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    stopPolling();
  };

  const fetchStatus = async () => {
    try {
      const response = await genericRepositoryService.getExportStatus(lessonVersionId);

      if (response.isSuccess && response.data) {
        handleSuccessfulResponse(response.data);
      } else {
        handleError(response.errorMessage || "Failed to fetch draft status");
      }
    } catch (err) {
      handleError("An error occurred while fetching draft status");
      console.error(err);
    }
  };

  const handleClose = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setStatus(null);
    setError(null);
    setIsComplete(false);
    setHasStartedProcessing(false);
    onClose();
  };

  const getExportLink = () => {
    if (status?.blobPath) {
      return status.blobPath;
    }
    return null;
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="export-status-modal-overlay">
      <div className="export-status-modal">
        <div className="export-status-modal-header">
          <h2>Draft Status</h2>
          <button className="export-status-close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="export-status-modal-body">
          {!isComplete && !error && (
            <div className="export-status-loading">
              <img
                src="https://i.giphy.com/media/v1.Y2lkPTc5MGI3NjExcDRoaXB3YjBmN3NrZ2l5YnZ4eW9yenRwM2NyZHduN2J1OGd5YmJobyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3oEjI6SIIHBdRxXI40/giphy.gif"
                alt="Processing..."
                className="export-status-gif"
              />
              <p className="export-status-text">{status?.status || "Processing your draft..."}</p>
            </div>
          )}

          {error && (
            <div className="export-status-error">
              <h3>Error</h3>
              <p>{error}</p>
              <button className="export-status-btn" onClick={handleClose}>
                Close
              </button>
            </div>
          )}

          {isComplete && !error && status && (
            <div className="export-status-complete">
              {status.isPublished && getExportLink() ? (
                <div className="export-status-success">
                  <div className="export-status-success-icon">
                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="40" cy="40" r="40" fill="#4CAF50" />
                      <path
                        d="M25 40L35 50L55 30"
                        stroke="white"
                        strokeWidth="5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h3>Draft Complete!</h3>
                  <p>Status: {status.status}</p>
                  {status.publishedDateUtc && <p>Drafted: {new Date(status.publishedDateUtc).toLocaleString()}</p>}
                  <button
                    className="export-status-btn"
                    onClick={() => {
                      handleClose();
                      globalThis.location.reload();
                    }}
                  >
                    Refresh
                  </button>
                </div>
              ) : (
                <div className="export-status-error">
                  <div className="export-status-error-icon">
                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="40" cy="40" r="40" fill="#f44336" />
                      <path d="M30 30L50 50M50 30L30 50" stroke="white" strokeWidth="5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <h3>Draft Failed</h3>
                  <p>{status.status || "The draft process did not complete successfully."}</p>
                  <button className="export-status-btn" onClick={handleClose}>
                    Close
                  </button>
                </div>
              )}
            </div>
          )}

          {status && !isComplete && (
            <div className="export-status-details">
              <p>
                <strong>Lesson Version ID:</strong> {status.lessonVersionId}
              </p>
              <p>
                <strong>Processing:</strong> {status.isProcessing ? "Yes" : "No"}
              </p>
              <p>
                <strong>Published:</strong> {status.isPublished ? "Yes" : "No"}
              </p>
              {status.isUploadedToLms && (
                <p>
                  <strong>Uploaded to LMS:</strong> Yes
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportStatusModal;
